import {
  Ban,
  CheckCircle2,
  Circle,
  CircleCheckBig,
  Eye,
  Loader2,
  MessageCircle,
  PauseCircle,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@reflex/ui/components/ui/tooltip';
import { cn } from '@reflex/ui/lib/utils';
import { ACTIVITY_CONFIG } from '@/lib/status-colors';
import { AGENT_MARK_CONFIG } from '@/lib/agent-marks';
import type { AgentActivity, AgentMark } from '@/lib/types';
import { AgentHoverPreview } from './AgentHoverPreview';

/**
 * The glyphs this column draws that run full bleed to 22 units of lucide's
 * 24-unit grid, rather than keeping to the 20-unit safe area most icons use. At
 * one box size they paint ~10% wider than their neighbours and land ~0.7px
 * closer to the row title, so without `icon-full-bleed` the gap between icon
 * and label changes with whatever state a row happens to be in.
 *
 * Only the mark glyphs are looked up here — the activity glyphs below name
 * their component inline, so they carry the class directly. Keep both in step
 * when a glyph changes.
 */
const FULL_BLEED_MARK_ICONS: ReadonlySet<LucideIcon> = new Set([Ban, CircleCheckBig, Eye]);

/**
 * Whether a mark should be shown in place of the derived activity. A mark is a
 * claim about a run that has stopped moving, so a run that is working owns its
 * icon outright — the spinner is live information the mark would hide. The
 * server clears marks at turn start, so this only covers the moment between the
 * turn starting and that write landing.
 */
function marksShown(activity: AgentActivity): boolean {
  return activity !== 'starting' && activity !== 'working';
}

/**
 * Expressive activity icon — spinning loader, check circle, etc.
 * Shared between the sidebar agent rows and the stream view header.
 *
 * When the agent carries a triage `mark` and is not working, the mark's icon
 * replaces the activity glyph: the row then reads as "a person classified this"
 * rather than "here is what the runner last did".
 *
 * When `agentId` is set, the icon becomes a hover trigger that reveals the
 * full agent preview popover (matching the user/org owner-chip previews) —
 * this takes precedence over `showTooltip`. Otherwise, when `showTooltip`
 * is true, the icon is wrapped in a Radix Tooltip that displays the
 * activity label on hover (content is portal-mounted and only rendered when
 * the tooltip opens).
 */
export function AgentActivityIcon({
  activity,
  mark,
  className,
  showTooltip = false,
  agentId,
}: {
  activity: AgentActivity;
  /** Triage mark to render in place of the activity glyph, when set. */
  mark?: AgentMark | null;
  className?: string;
  showTooltip?: boolean;
  /** When set, hovering the icon opens the rich agent preview popover. */
  agentId?: string;
}) {
  const base = cn('shrink-0', className ?? 'icon-md');
  // Every glyph here but the spinner runs full bleed, so it needs trimming back
  // to the safe area to sit at the same ink size as the rest of the column.
  const fullBleed = cn(base, 'icon-full-bleed');
  const shownMark = mark && marksShown(activity) ? mark : null;
  let iconEl: React.ReactElement;
  if (shownMark) {
    const config = AGENT_MARK_CONFIG[shownMark];
    const markBase = FULL_BLEED_MARK_ICONS.has(config.icon) ? fullBleed : base;
    iconEl = <config.icon className={cn(markBase, config.className)} />;
  } else {
    switch (activity) {
      case 'starting':
        iconEl = <Loader2 className={cn(base, 'animate-spin text-status-warning')} />;
        break;
      case 'working':
        iconEl = <Loader2 className={cn(base, 'animate-spin text-status-success')} />;
        break;
      case 'waiting_input':
        iconEl = <MessageCircle className={cn(fullBleed, 'animate-pulse text-status-warning')} />;
        break;
      case 'completed':
        iconEl = <CheckCircle2 className={cn(fullBleed, 'text-status-info')} />;
        break;
      case 'error':
        iconEl = <XCircle className={cn(fullBleed, 'text-status-error')} />;
        break;
      case 'interrupted':
        iconEl = <PauseCircle className={cn(fullBleed, 'text-status-warning')} />;
        break;
      case 'idle':
      case 'stopped':
        iconEl = <Circle className={cn(fullBleed, 'text-muted-foreground')} />;
        break;
    }
  }

  const icon = (
    <span
      data-testid="agent-activity-icon"
      data-activity={activity}
      data-mark={shownMark ?? undefined}
      className="inline-flex"
    >
      {iconEl}
    </span>
  );

  if (agentId) return <AgentHoverPreview agentId={agentId}>{icon}</AgentHoverPreview>;

  if (!showTooltip) return icon;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0">{icon}</span>
      </TooltipTrigger>
      <TooltipContent side="top">
        {shownMark ? AGENT_MARK_CONFIG[shownMark].label : ACTIVITY_CONFIG[activity].sidebar.text}
      </TooltipContent>
    </Tooltip>
  );
}
